import { diffObjectsDeep } from '@caresend/utils';
import cloneDeep from 'lodash.clonedeep';
import { cleanInputWithInitializerDeep } from '../helpers/helpers.js';
import { injectNullsDeep } from '../utils/injectNulls.js';
import { WorkflowItemStatus } from './model.js';

const initUniqueWorkflowItemID = ({ id, parentContext, childContext = 'default', }) => `${id}-${parentContext}-${childContext}`;
const getUniqueWorkflowItemID = (item) => {
    var _a, _b, _c, _d;
    return initUniqueWorkflowItemID({
        id: item.id.toString(),
        parentContext: (_b = (_a = item.recordMeta.parent) === null || _a === void 0 ? void 0 : _a.context) !== null && _b !== void 0 ? _b : 'default',
        childContext: (_d = (_c = item.recordMeta.childWorkflow) === null || _c === void 0 ? void 0 : _c.context) !== null && _d !== void 0 ? _d : 'default',
    });
};
const isKeyOfRecord = (key, record) => !!record && key in record;
const initialWorkflowItem = {
    id: '',
    recordMeta: {
        referenceID: '',
        storeRef: '',
        parent: {
            context: '',
            referenceID: '',
            storeRef: '',
        },
    },
    status: {
        status: WorkflowItemStatus.PENDING,
        timestamp: 0,
    },
    options: {},
};
const getWorkflowItemInitializer = (referenceID, storeRef, parent) => (id, options = {}, childWorkflowOptions) => ({
    ...initialWorkflowItem,
    id,
    recordMeta: {
        referenceID,
        storeRef,
        parent,
        childWorkflow: childWorkflowOptions,
    },
    status: {
        status: WorkflowItemStatus.PENDING,
        timestamp: 0,
    },
    options: {
        disabled: false,
        ...options,
    },
});
const getCommittedWorkflow = (element, parentReferenceID, context) => {
    var _a, _b, _c;
    return cloneDeep((_c = (_b = (_a = element.workflows) === null || _a === void 0 ? void 0 : _a[parentReferenceID]) === null || _b === void 0 ? void 0 : _b[context]) !== null && _c !== void 0 ? _c : []);
};
const getPriority = (workflow, priorities, item) => {
    var _a, _b;
    return (_b = (_a = priorities[getUniqueWorkflowItemID(item)]) !== null && _a !== void 0 ? _a : workflow.indexOf(item)) !== null && _b !== void 0 ? _b : workflow.length;
};
const sortWorkflowByPriority = (workflow, priorities) => workflow.sort((a, b) => {
    const aPriority = getPriority(workflow, priorities, a);
    const bPriority = getPriority(workflow, priorities, b);
    return aPriority - bPriority;
});
const existingWorkflowMap = (workflow = []) => {
    const map = new Map();
    workflow.forEach((item) => {
        map.set(getUniqueWorkflowItemID(item), item);
    });
    return map;
};
const existsButShouldUpdate = (item, workflowMap) => {
    var _a, _b, _c, _d;
    const id = getUniqueWorkflowItemID(item);
    const exists = workflowMap.has(id);
    if (!exists)
        return false;
    const existingItem = workflowMap.get(id);
    const newRecordMeta = injectNullsDeep((_a = item.recordMeta) !== null && _a !== void 0 ? _a : {});
    const newOptions = injectNullsDeep((_b = item.options) !== null && _b !== void 0 ? _b : {});
    const oldRecordMeta = injectNullsDeep((_c = existingItem === null || existingItem === void 0 ? void 0 : existingItem.recordMeta) !== null && _c !== void 0 ? _c : {});
    const oldOptions = injectNullsDeep((_d = existingItem === null || existingItem === void 0 ? void 0 : existingItem.options) !== null && _d !== void 0 ? _d : {});
    const diffs = {
        ...diffObjectsDeep(oldRecordMeta, newRecordMeta, [], true),
        ...diffObjectsDeep(oldOptions, newOptions, [], true),
    };
    return Object.keys(diffs).length !== 0;
};
const maybeAddToMap = (_item, workflowMap) => {
    const item = cleanInputWithInitializerDeep(_item, initialWorkflowItem);
    const id = getUniqueWorkflowItemID(item);
    if (existsButShouldUpdate(item, workflowMap)) {
        const newItem = workflowMap.get(id);
        if (!newItem)
            return workflowMap;
        /** Merge the options and recordMeta */
        newItem.recordMeta = {
            ...newItem.recordMeta,
            ...item.recordMeta,
        };
        newItem.options = {
            ...newItem.options,
            ...item.options,
        };
        return workflowMap.set(id, newItem);
    }
    if (workflowMap.has(id)) {
        return workflowMap;
    }
    workflowMap.set(id, item);
    return workflowMap;
};

export { existingWorkflowMap, existsButShouldUpdate, getCommittedWorkflow, getPriority, getUniqueWorkflowItemID, getWorkflowItemInitializer, initUniqueWorkflowItemID, isKeyOfRecord, maybeAddToMap, sortWorkflowByPriority };
